/**
 * The Mystics tab (2026-10-26, Claude outputs/mystics-tab-2026-10-26/DESIGN.md,
 * r2 with the r3 and r3b addenda). The words and the pure parts, so the page
 * and its specs share them. The server decides the orders and who counts
 * (GET /reader/mystics-tab/); the app picks among them and keeps the partner
 * rule when a star changes who is visible.
 */
export const AIDA = 'iam'
export const EXPLAIN = 'Each Mystic is an AI reader with its own deck and voice. Try one; keep the ones you like with ☆.'
export const PLACEHOLDER = 'Mystic Aida can help you find one…'
export const NO_MATCH = 'Nothing clear for that one. Try it in other words, or pick something from the box.'
export const HOME_STAYS = 'Your home Mystic stays in Your Mystics. Make another Mystic home first.'
export const EMPTY_SLOT = 'Star a Mystic below to keep it here'
export const SORT_KEY = 'mystics.sort'

// The label says how the list is sorted; there is no line under it (Chris 2026-11-03).
export const SORTS = [
  { key: 'trending', label: 'Trending this week' },
  { key: 'loved', label: 'Most loved' },
  { key: 'new', label: 'Newest first' },
  { key: 'az', label: 'A to Z' },
  { key: 'shuffle', label: 'Today’s shuffle' },
]
const BY_KEY = Object.fromEntries(SORTS.map(s => [s.key, s]))
const TOPIC_LABELS = { love: 'Love', money: 'Money', work: 'Work', health: 'Health', family: 'Friends & family', self: 'Self', choices: 'Choices' }

export const sortLabel = (key) => (BY_KEY[key] || BY_KEY.shuffle).label
export const topicLabel = (key) => TOPIC_LABELS[key] || key

// "For you" is parked (Chris, 2026-10-27): see Roadmap, "Mystics by sensibility".

/** The server's default, unless this device chose a sort before. */
export function startSort (sort, saved) {
  if (saved && BY_KEY[saved]) return saved
  return (sort && BY_KEY[sort.default]) ? sort.default : 'shuffle'
}

/** Where we choose the order: Mystic Aida never leads for someone whose home is another Mystic. */
export function partnerRule (list, home) {
  const out = [...list]
  if (!home || home === AIDA || out[0] !== AIDA || out.length < 2) return out
  out.splice(0, 1)
  out.splice(1, 0, AIDA)
  return out
}

/** Everyone here: the chosen order without Your Mystics. */
export function everyone (orders, sort, mine) {
  const own = new Set((mine && mine.order) || [])
  const list = ((orders && orders[sort]) || []).filter(s => !own.has(s))
  return sort === 'shuffle' ? partnerRule(list, mine && mine.home) : list
}

export function move (order, sub, d) {
  const out = [...order]
  const i = out.indexOf(sub), j = i + d
  if (i < 0 || j < 0 || j >= out.length) return out
  out.splice(i, 1)
  out.splice(j, 0, sub)
  return out
}

export function withStar (mine, sub) {
  if (mine.order.includes(sub)) return mine
  return { ...mine, order: [...mine.order, sub] }
}

/** null: the home Mystic can't be un-starred. */
export function withoutStar (mine, sub) {
  if (sub === mine.home) return null
  return { ...mine, order: mine.order.filter(s => s !== sub) }
}

export function shortName (m) {
  const name = (m && m.name) || ''
  const words = name.split(/\s+/).filter(Boolean)
  return words.length > 2 ? words[0] : name
}

const words = (q) => String(q || '').toLowerCase().replace(/[^a-z' -]/g, ' ').split(/\s+/).filter(Boolean)

export function namesIn (q, readers) {
  const t = String(q || '').toLowerCase().trim()
  if (t.length < 2) return []
  return (readers || []).filter(m => {
    const n = (m.name || '').toLowerCase()
    return n.split(/[\s’'-]+/).some(w => w.startsWith(t)) || n.includes(t)
  })
}

export function vibesIn (q, vibes) {
  const ws = words(q)
  const t = String(q || '').toLowerCase().trim()
  let out = (vibes || []).filter(v => ws.some(w => (v.words || []).includes(w)))
  if (!out.length && t.length > 1) out = (vibes || []).filter(v => v.label.toLowerCase().startsWith(t))
  return out
}

/** What the box suggests while typing. `server`: the word list's answer (no model). */
export function suggestions (q, readers, vibes, server = {}) {
  const text = String(q || '').trim()
  const vs = vibesIn(text, vibes)
  const out = {
    mystics: namesIn(text, readers).slice(0, 4),
    topics: (server.topics || []).map(k => ({ key: k, label: topicLabel(k) })),
    vibes: vs.map(v => ({ key: v.key, label: v.label, count: (readers || []).filter(m => (m.vibes || []).includes(v.key)).length })),
    practice: server.practice ? `No Mystic reads ${server.practice} yet. Everyone here reads tarot.` : '',
    foot: '',
  }
  const any = out.mystics.length || out.topics.length || out.vibes.length || out.practice
  out.foot = any ? `Press Enter to search “${text}”.` : `Press Enter and Mystic Aida will look for who reads “${text}” well.`
  return out
}

/** Enter: a short name stays a name (no server); anything else is asked. */
export function routeOf (q, readers) {
  const text = String(q || '').trim()
  if (!text) return { kind: 'clear' }
  const named = namesIn(text, readers)
  if (named.length && text.split(/\s+/).length <= 2) return { kind: 'name', subs: named.map(m => m.sub_domain) }
  return { kind: 'ask', q: text }
}

/** The card's fact line: spread · price (after the unlock) · ★ from three ratings. */
export function factLine (m, { pricesVisible = true } = {}) {
  const out = []
  const r = m && m.reading
  if (r && r.spread_name) out.push(r.spread_name)
  if (pricesVisible && r && r.carats !== undefined && r.carats !== null && Number(r.carats) > 0) {
    out.push(`${Number(r.carats)} ✦`)
  }
  const s = (m && m.stats) || {}
  if (Number(s.rating_count) >= 3 && Number(s.rating) > 0) out.push(`★ ${Number(s.rating).toFixed(1)}`)
  return out
}

export function savedSort () {
  try { return localStorage.getItem(SORT_KEY) } catch (e) { return null }
}
export function saveSort (key) {
  try { localStorage.setItem(SORT_KEY, key) } catch (e) { /* private mode: fine */ }
}
